import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./SearchModal.module.css";

import { EagleItem } from "../EagleItem/EagleItem";
import SearchIcon from "../Search/Group17523.svg";
import ChevLeft from "../EagleProfileHeader/Images/path.svg";
import Ellipse from "./Ellipse701.svg";
import Circle from "./Group3541.svg";
import ClearBtn from "./Clear_Btn.svg";
import { useTranslation } from "react-i18next";

const trendColors = { up: "#43BA32", same: "#FF9A00", down: "#ED1F24" };
const getColor = (stat) => trendColors[stat?.trend] || "#ED1F24";
const pad = (v) => (v != null ? v.toString().padStart(2, "0") : "00");
const splitDisplayName = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: "", lastName: "" };
  }

  if (parts.length === 1) {
    return { firstName: "", lastName: parts[0] };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

function StatColumn({ label, value, color }) {
  return (
    <div className={styles.statCol}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue} style={{ color }}>
        {value}
      </span>
    </div>
  );
}

export function SearchModal({ isOpen, setIsOpen, items = [] }) {
  const [value, setValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const { t } = useTranslation();

  const onClose = () => {
    setIsOpen(false);
    setValue("");
    setSelectedItem(null);
  };

  const onClear = () => setValue("");

  const onBack = () => setSelectedItem(null);

  const filtered = value.trim()
    ? items.filter((item) =>
        item.name.toLowerCase().includes(value.toLowerCase())
      )
    : [];

  const selectedNameParts = splitDisplayName(selectedItem?.name);

  return (
    <>
      {isOpen && (
        <div className={styles.overlay}>
          {/* ── Search bar row ── */}
          <div className={styles.row}>
            <div className={styles.searchBox}>
              <img src={SearchIcon} className={styles.searchIcon} alt="" />
              <input
                className={styles.input}
                type="text"
                placeholder={t("Enter Text")}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSelectedItem(null);
                }}
                autoFocus
              />
              {value.length > 0 && (
                <img
                  src={ClearBtn}
                  className={styles.clearBtn}
                  onClick={onClear}
                  alt="Clear"
                />
              )}
            </div>
            <div className={styles.closeBtn} onClick={onClose}>
              <img src={Ellipse} className={styles.ellipse} />
              <img src={Circle} className={styles.circle} />
            </div>
          </div>

          {/* ── Detail view ── */}
          {selectedItem && (
            <div className={styles.detail}>
              <div className={styles.detailHeader}>
                {/* Left: back + avatar + name */}
                <div className={styles.detailLeft}>
                  <img
                    src={ChevLeft}
                    className={styles.chevron}
                    onClick={onBack}
                    alt="Back"
                  />
                  <div className={styles.detailAvatarWrap}>
                    {selectedItem.avatar[0] && (
                      <img
                        src={selectedItem.avatar[0]}
                        className={styles.detailAvatar}
                        alt=""
                      />
                    )}
                    {selectedItem.avatar[1] && (
                      <img
                        src={selectedItem.avatar[1]}
                        className={styles.detailAvatarOverlap}
                        alt=""
                      />
                    )}
                  </div>
                  <div className={styles.detailNameStack}>
                    <div className={styles.detailNameBlock}>
                      {selectedNameParts.firstName && (
                        <span className={styles.detailFirstName}>
                          {selectedNameParts.firstName}
                        </span>
                      )}
                      <span className={styles.detailLastName}>
                        {selectedNameParts.lastName}
                      </span>
                    </div>
                    <span className={styles.detailIboLabel}>{t("IBO")}</span>
                  </div>
                </div>

                {/* Right: IBO number + Share button */}
                <div className={styles.detailRight}>
                  <div className={styles.detailIboRow}>
                    <span className={styles.detailIboPrefix}>IBO:&nbsp;</span>
                    <span className={styles.detailIboNumber}>
                      {selectedItem.ibo}
                    </span>
                  </div>
                  <button
                    className={styles.shareBtn}
                    onClick={() => {
                      selectedItem.onClick();
                      onClose();
                    }}
                  >
                    {t("Detail")}
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className={styles.detailDivider} />

              {/* Stats row */}
              <div className={styles.statsRow}>
                <StatColumn
                  label={t("PV")}
                  value={
                    selectedItem.totalBonus?.value != null
                      ? pad(selectedItem.totalBonus.value)
                      : "—"
                  }
                  color={getColor(selectedItem.totalBonus)}
                />
                <StatColumn
                  label={t("VCS")}
                  value={
                    selectedItem.totalPremier?.value != null
                      ? `${pad(selectedItem.totalPremier.value)}%`
                      : "—"
                  }
                  color={getColor(selectedItem.totalPremier)}
                />
                <StatColumn
                  label={t("Legs")}
                  value={
                    selectedItem.totalMajorFunctions?.value != null
                      ? pad(selectedItem.totalMajorFunctions.value)
                      : "—"
                  }
                  color={getColor(selectedItem.totalMajorFunctions)}
                />
                <StatColumn
                  label={t("Qualification")}
                  value={t("None")}
                  color={"#ED1F24"}
                />
              </div>
            </div>
          )}

          {/* ── Results list ── */}
          {!selectedItem && filtered.length > 0 && (
            <div className={styles.results}>
              {filtered.map((item, i) => (
                <div
                  key={i}
                  className={styles.resultItem}
                  onClick={() => setSelectedItem(item)}
                >
                  <EagleItem
                    name={item.name}
                    avatar={item.avatar}
                    totalBonus={item.totalBonus}
                    totalPremier={item.totalPremier}
                    totalMajorFunctions={item.totalMajorFunctions}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  items: PropTypes.array,
};
